// VocabularySubcategories.js - Updated component for multi-course vocabulary subcategory selection
import React, { useState, useEffect } from 'react';
import { VocabularyStorage } from './VocabularyStorage';

/**
 * Component for displaying and selecting vocabulary subcategories
 * Now supports multiple courses (ASA101, ASA103, etc.)
 */
function VocabularySubcategories({ onSelectSubcategory, selectedCourse }) {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedSession, setSavedSession] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // Define course-specific vocabulary subcategories
  useEffect(() => {
    let vocabSubcategories = [];
    
    if (selectedCourse === 'ASA101') {
      vocabSubcategories = [
        { id: 1, name: 'A-C', description: 'Aback to Cunningham', termCount: 65 },
        { id: 2, name: 'D-G', description: 'Daggerboard to Gust', termCount: 40 },
        { id: 3, name: 'H-K', description: 'Halyard to Knot', termCount: 33 },
        { id: 4, name: 'L-N', description: 'Lay to Navigate', termCount: 34 },
        { id: 5, name: 'O-S', description: 'Off the wind to Surge', termCount: 59 },
        { id: 6, name: 'T-Z', description: 'Tack to Zephyr', termCount: 30 }
      ];
    } else if (selectedCourse === 'ASA103') {
      vocabSubcategories = [
        { id: 1, name: 'Part 1', description: 'Accommodations to Flood tide', termCount: 26 },
        { id: 2, name: 'Part 2', description: 'Fore-reach to Rope clutch', termCount: 26 },
        { id: 3, name: 'Part 3', description: 'Safety harness to Windlass', termCount: 26 }
      ];
    } else {
      // Default fallback or other courses
      vocabSubcategories = [
        { id: 1, name: 'Part 1', description: 'Vocabulary Section 1', termCount: 0 },
        { id: 2, name: 'Part 2', description: 'Vocabulary Section 2', termCount: 0 }
      ];
    }
    
    setSubcategories(vocabSubcategories);
    setLoading(false);
  }, [selectedCourse]);
  
  // Check for saved sessions - now course-specific
  useEffect(() => {
    if (selectedCourse) {
      // Create course-specific storage key
      const storagePrefix = `${selectedCourse.toLowerCase()}_vocab`;
      
      // Check for last active subcategory for this course
      const lastSubcategoryId = VocabularyStorage.getLastActiveSubcategory(storagePrefix);
      if (lastSubcategoryId) {
        const savedSession = VocabularyStorage.loadQuizSession(lastSubcategoryId, storagePrefix);
        if (savedSession) {
          setSavedSession({
            subcategoryId: lastSubcategoryId,
            session: savedSession
          });
        }
      }
    }
  }, [selectedCourse]);
  
  // Handle subcategory selection
  const handleSubcategorySelect = (subcategoryId) => {
    if (onSelectSubcategory) {
      onSelectSubcategory(subcategoryId);
    }
  };
  
  // Resume a saved session
  const handleResumeSession = () => {
    if (savedSession && onSelectSubcategory) {
      onSelectSubcategory(savedSession.subcategoryId, savedSession.session);
    }
  };
  
  // Handle reset of all progress for current course
  const handleResetProgress = () => {
    if (showResetConfirm) {
      // Perform the actual reset for this course
      const storagePrefix = `${selectedCourse.toLowerCase()}_vocab`;
      VocabularyStorage.clearAllProgress(storagePrefix);
      
      // Reset UI state
      setSavedSession(null);
      
      // Hide confirmation
      setShowResetConfirm(false);
      
      // Show success message
      alert(`All ${selectedCourse} vocabulary progress has been reset`);
    } else {
      // Show confirmation first
      setShowResetConfirm(true);
    }
  };
  
  // Get course-specific title and styling
  const getCourseInfo = () => {
    switch (selectedCourse) {
      case 'ASA101':
        return {
          title: 'ASA 101 Sailing Vocabulary',
          description: 'Essential sailing terms and definitions',
          color: '#1e40af'
        };
      case 'ASA103':
        return {
          title: 'ASA 103 Coastal Cruising Vocabulary',
          description: 'Advanced sailing terminology for coastal cruising',
          color: '#059669'
        };
      default:
        return {
          title: 'Sailing Vocabulary',
          description: 'Sailing terms and definitions',
          color: '#6b7280'
        };
    }
  };
  
  const courseInfo = getCourseInfo();
  
  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          margin: '0 auto',
          borderWidth: '3px', 
          borderStyle: 'solid',
          borderColor: '#e5e7eb',
          borderTopColor: courseInfo.color,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '10px' }}>Loading vocabulary sections...</p>
      </div>
    );
  }
  
  return (
    <div>
      {/* Resume session button - moved to top */}
      {savedSession && (
        <div 
          style={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#10b981',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px',
            backgroundColor: '#d1fae5',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all 0.2s ease'
          }}
          onClick={handleResumeSession}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#a7f3d0';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#d1fae5';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div>
            <div style={{ fontWeight: 'bold', color: '#065f46', marginBottom: '4px' }}>
              Resume Previous Session
            </div>
            <div style={{ fontSize: '14px', color: '#059669' }}>
              {
                subcategories.find(s => s.id === savedSession.subcategoryId)?.name || 'Vocabulary'
              } - Question {savedSession.session.currentQuestionIndex + 1}
            </div>
          </div>
          
          <div style={{
            backgroundColor: '#10b981',
            color: 'white',
            borderRadius: '4px',
            padding: '8px 16px',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Resume
          </div>
        </div>
      )}

      <h3 style={{ 
        fontSize: '20px', 
        fontWeight: 'bold', 
        marginBottom: '8px',
        color: courseInfo.color,
        borderBottom: `2px solid ${courseInfo.color}`,
        paddingBottom: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>{courseInfo.title}</span>
        
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Back to Categories button */}
          <button
            onClick={() => onSelectSubcategory('back')}
            style={{
              fontSize: '14px',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            ← Back to Categories
          </button>
          
          {/* Reset button */}
          <button
            onClick={handleResetProgress}
            style={{
              fontSize: '14px',
              backgroundColor: showResetConfirm ? '#ef4444' : '#f3f4f6',
              color: showResetConfirm ? 'white' : '#6b7280',
              border: showResetConfirm ? '1px solid #dc2626' : '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {showResetConfirm ? 'Confirm Reset' : 'Start Over'}
          </button>
        </div>
      </h3>
      
      {/* Course description */}
      <p style={{ 
        marginBottom: '20px', 
        color: '#6b7280', 
        fontSize: '14px',
        fontStyle: 'italic'
      }}>
        {courseInfo.description}
      </p>
      
      {/* Subcategory grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {subcategories.map(subcategory => {
          // Use course-specific colors for the cards
          const cardColor = courseInfo.color;
          const lightBg = selectedCourse === 'ASA101' ? '#eff6ff' : selectedCourse === 'ASA103' ? '#ecfdf5' : '#f9fafb';
          const hoverBg = selectedCourse === 'ASA101' ? '#dbeafe' : selectedCourse === 'ASA103' ? '#d1fae5' : '#f3f4f6';
          
          return (
            <div 
              key={subcategory.id}
              style={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: `${cardColor}40`,
                borderRadius: '8px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: lightBg,
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={() => handleSubcategorySelect(subcategory.id)}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = hoverBg;
                e.currentTarget.style.borderColor = cardColor;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = lightBg;
                e.currentTarget.style.borderColor = `${cardColor}40`;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h4 style={{ 
                color: cardColor, 
                marginBottom: '8px', 
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                {subcategory.name}
              </h4>
              
              <p style={{ color: cardColor, fontSize: '14px', marginBottom: '8px', opacity: 0.8 }}>
                {subcategory.description}
              </p>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#6b7280', fontSize: '13px' }}>
                  {subcategory.termCount} terms
                </span>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Course-specific additional information */}
      {selectedCourse === 'ASA103' && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '6px',
          padding: '16px',
          marginTop: '20px'
        }}>
          <h4 style={{ color: '#0369a1', marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
            About ASA 103 Vocabulary
          </h4>
          <p style={{ color: '#0c4a6e', fontSize: '14px', margin: 0, lineHeight: '1.4' }}>
            These 78 terms represent the additional vocabulary introduced in ASA 103 Basic Coastal Cruising 
            that builds upon the foundational terms from ASA 101. Focus on navigation, weather, and boat systems terminology.
          </p>
        </div>
      )}
    </div>
  );
}

export default VocabularySubcategories;